import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import {
  Card,
  Col,
  Row,
  Tabs,
  Drawer,
  DatePicker,
  Image,
  Modal,
} from "antd";
import CustomList from "../../../../components/List";
import ListItem from "../../../../components/ListItem";
import { usePackagePCTChallenges, usePackagePITChallenges, usePackageCSCChallenges } from "../../../../../hooks/useDataHooks";
import { usePCTDateFilter } from "../../../../../hooks/usePCTDateFilter";
import { usePITDateFilter } from "../../../../../hooks/usePITDateFilter";
import { useCSCDateFilter } from "../../../../../hooks/useCSCDateFilter";
import { useProcuringEntity } from "../../../../../hooks/useProcuringEntity";
import { isoDateToHumanReadableDate } from "../../../../../Util";
import ActionBar from "../../../../components/ActionBar";

const { TabPane } = Tabs;

const useColumnLayouts = (activeTab) => {
  return useMemo(() => {
    
    if (activeTab === "PCT") {
      return {
        subProjectLayout: { 
          xxl: 5, xl: 5, lg: 5, md: 6, sm: 24, xs: 24 
        },
        challengesLayout: { 
          xxl: 6, xl: 6, lg: 6, md: 6, sm: 24, xs: 24 
        },
        actionTakenLayout: { 
          xxl: 5, xl: 5, lg: 5, md: 5, sm: 24, xs: 24 
        },
        reportDateLayout: { 
          xxl: 3, xl: 3, lg: 3, md: 3, sm: 24, xs: 24 
        }
      };
    }
  
    if (activeTab === "PIT") {
      return {
        subProjectLayout: { 
          xxl: 5, xl: 5, lg: 5, md: 6, sm: 24, xs: 24 
        },
        challengesLayout: { 
          xxl: 6, xl: 6, lg: 6, md: 6, sm: 24, xs: 24 
        },
        actionTakenLayout: { 
          xxl: 5, xl: 5, lg: 5, md: 5, sm: 24, xs: 24 
        },
        reportDateLayout: { 
          xxl: 3, xl: 3, lg: 3, md: 3, sm: 24, xs: 24 
        },
      };
    }
    
    return {
      subProjectLayout: { xxl: 8, xl: 8, lg: 8, md: 8, sm: 24, xs: 24 },
      challengesLayout: { xxl: 8, xl: 8, lg: 8, md: 8, sm: 24, xs: 24 },
      wayForward: { xxl: 6, xl: 6, lg: 6, md: 6, sm: 24, xs: 24 },
    };
  }, [activeTab]);
};

const tabStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 500,
};

const iconStyle = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "9px",
  fontWeight: "700",
  color: "white",
};

const ChallengesImplementations = ({ match }) => {
  const packageId = match?.params?.packageId;
  const procuringEntityId = match?.params?.procuringEntityId;

  const [singlePreviewVisible, setSinglePreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [activeTab, setActiveTab] = useState("CSC");

  const { councilId, loading: procuringEntityLoading } = useProcuringEntity(procuringEntityId);

  const { 
    startDate: pctStartDate,
    endDate: pctEndDate,
    updateDateRange: updatePCTDateRange,
  } = usePCTDateFilter();
  
  const { 
    startDate: pitStartDate,
    endDate: pitEndDate,
    updateDateRange: updatePITDateRange,
  } = usePITDateFilter();

  const { 
    startDate: cscStartDate,
    endDate: cscEndDate,
  } = useCSCDateFilter();

  const effectiveCouncilId = procuringEntityLoading ? undefined : (councilId ?? null);
  
  const { pctChallenges, loading: pctLoading } = usePackagePCTChallenges(
    packageId, 
    pctStartDate, 
    pctEndDate, 
    effectiveCouncilId,
    procuringEntityLoading
  );
  const { pitChallenges, loading: pitLoading } = usePackagePITChallenges(
    packageId, 
    pitStartDate, 
    pitEndDate, 
    effectiveCouncilId,
    procuringEntityLoading
  );
  const { cscChallenges, loading: cscLoading } = usePackageCSCChallenges(
    packageId, 
    cscStartDate, 
    cscEndDate, 
    effectiveCouncilId,
    procuringEntityLoading
  );

  const { subProjectLayout, challengesLayout, actionTakenLayout, wayForward, reportDateLayout } = useColumnLayouts(activeTab);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const originalError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      if (message.includes('ResizeObserver loop completed with undelivered notifications')) {
        return true;
      }
      if (originalError) {
        return originalError(message, source, lineno, colno, error);
      }
      return false;
    };

    const handleUnhandledRejection = (event) => {
      if (event.reason && event.reason.message && 
          event.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        event.preventDefault();
      }
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.onerror = originalError;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const handleRowClick = (item) => {
    setActiveItem(item);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setActiveItem(null);
  };

  const openSinglePreview = (url) => {
    setPreviewImage(url);
    setSinglePreviewVisible(true);
  };

  const handleSingleCancel = () => {
    setSinglePreviewVisible(false);
  };

  const getColumns = () => {
    if (activeTab === "PCT") {
      return [
        { ...subProjectLayout, header: "Sub-Project" },
        { ...challengesLayout, header: "Challenge" },
        { ...actionTakenLayout, header: "Action Taken" },
        { ...reportDateLayout, header: "Report Date" }
      ];
    }
    if (activeTab === "PIT") {
      return [
        { ...subProjectLayout, header: "Sub-Project" },
        { ...challengesLayout, header: "Challenge" },
        { ...actionTakenLayout, header: "Action Taken" },
        { ...reportDateLayout, header: "Report Date" }
      ];
    }
    return [
      { ...subProjectLayout, header: "Sub-Project" },
      { ...challengesLayout, header: "Challenge" },
      { ...wayForward, header: "Action Taken" },
    ];
  };

  const handlePCTDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      updatePCTDateRange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    }
  };

  const handlePITDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      updatePITDateRange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    }
  };

  const getTabContent = (key) => {
    switch (key) {
      case "CSC":
        return (
          <div className="table-title-container">
            <div>
              <h2 className="table-title">
                CSC CHALLENGES & WAY FORWARD
              </h2>
              <h4 
              style={{ margin: "0" }}
              className="table-sub-title">
                Construction Supervision Consultant Report
              </h4>
            </div>
            <p className="table-date-picker">Reported on {isoDateToHumanReadableDate(cscStartDate)} - {isoDateToHumanReadableDate(cscEndDate) || 'No date available'}</p>
          </div>
        );
      case "PIT":
        return (
          <div>
            <div
              style={{
                display: "flex",
                padding: "10px",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "0",
              }}
            >
              <div>
                <h2 className="table-title">
                  PIT CHALLENGES & WAY FORWARD
                </h2>
                <h4 
                style={{ margin: "0" }}
                className="table-sub-title">
                  Project Implementation Team Report
                </h4>
              </div>
              <div style={{ marginRight: "20%" }}>
                <DatePicker.RangePicker
                  value={pitStartDate && pitEndDate ? [moment(pitStartDate), moment(pitEndDate)] : null}
                  onChange={handlePITDateChange}
                  placeholder={["Start Date", "End Date"]}
                  size="middle"
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>
        );
      case "PCT":
        return (
          <div>
            <div
              style={{
                  display: "flex",
                padding: "10px",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "0",
              }}
            >
              <div>
                <h2 className="table-title">
                  PCT CHALLENGES & ACTIONS TAKEN
                </h2>
                <h4 
                style={{ margin: "0" }}
                className="table-sub-title">
                  Project Coordination Team Report
                </h4>
              </div>
              <div style={{ marginRight: "20%" }}>
                <DatePicker.RangePicker
                  value={pctStartDate && pctEndDate ? [moment(pctStartDate), moment(pctEndDate)] : null}
                  onChange={handlePCTDateChange}
                  placeholder={["Start Date", "End Date"]}
                  size="middle"
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getTabData = () => {
    switch (activeTab) {
      case "PCT":
        return pctChallenges && pctChallenges.length > 0 ? pctChallenges : [];
      case "PIT":
        return pitChallenges && pitChallenges.length > 0 ? pitChallenges : [];
      case "CSC":
        return cscChallenges && cscChallenges.length > 0 ? cscChallenges : [];
      default:
        return [];
    }
  };

  const getLoadingState = () => {
    switch (activeTab) {
      case "PCT":
        return pctLoading;
      case "PIT":
        return pitLoading;
      case "CSC":
        return cscLoading;
      default:
        return false;
    }
  };
  

  const tabData = getTabData();
  const isLoading = getLoadingState();

  return (
    <div style={{ padding: "16px" }}>

      <ActionBar
        actionButtonProp={{
          title: '',
        }}
      />

      <Card 
        style={{ border: "none", boxShadow: "none" }} 
        className="custom-container"
        hoverable={false}
        bordered={false}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} className="prst-tabs">
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "CSC" ? "#38A169" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#38A169" }} className="tab-icon">
                  CSC
                </span>
                Challenges - CSC
              </span>
            }
            key="CSC"
          >
            {getTabContent("CSC")}
            {tabData.length === 0 && !isLoading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: '#666',
                fontSize: '16px'
              }}>
                No challenges data available for the selected criteria.
              </div>
            ) : (
              <div className="prst-table">
                  <CustomList
                    itemName="Challenges"
                    items={tabData}
                    page={1}
                    itemCount={tabData.length}
                    loading={isLoading}
                    headerLayout={getColumns()}
                    renderListItem={({ item }) => (
                    <ListItem
                      key={item.id}
                      name={`CSC Challenge #${item.id}`}
                      item={item}
                      isPackage={false}
                      onClick={() => handleRowClick(item)}
                      style={{ marginBottom: 12, cursor: "pointer" }}
                    >
                      <Row gutter={[8, 0]} style={{ width: '100%' }}>
                        <Col {...subProjectLayout} className="contentEllipse">
                          <div className="mobile-label">Sub-Project</div>
                          {item?.sub_project && item?.sub_project?.length > 30
                            ? item?.sub_project?.slice(0, 30) + "..."
                            : item?.sub_project || 'N/A'}
                        </Col>
                        <Col {...challengesLayout} className="contentEllipse">
                          <div className="mobile-label">Challenge</div>
                          {item?.challenge && item?.challenge?.length > 40
                            ? item?.challenge?.slice(0, 40) + "..."
                            : item?.challenge || 'No challenge description'}
                        </Col>
                        <Col {...wayForward} className="contentEllipse">
                          <div className="mobile-label">Action Taken</div>
                          {item?.action_taken && item?.action_taken?.length > 40
                            ? item?.action_taken?.slice(0, 40) + "..."
                            : item?.action_taken || 'No action taken yet'}
                        </Col>
                        <button 
                          className="view-card-details" 
                          onClick={() => handleRowClick(item)}
                        >
                          View Details
                        </button>
                      </Row>
                    </ListItem>
                  )}
                  showToolbar={false}
                  />
              </div>
            )}
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PIT" ? "#38A169" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#38A169" }} className="tab-icon">
                  PIT
                </span>
                PIT
              </span>
            }
            key="PIT"
          >
            {getTabContent("PIT")}
            <div className="pct-table-container">
              {tabData.length === 0 && !isLoading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  color: '#666',
                  fontSize: '16px'
                }}>
                  No challenges data available for the selected criteria.
                </div>
              ) : (
                <CustomList
                itemName="Challenges"
                items={tabData}
                page={1}
                itemCount={tabData.length}
                loading={isLoading}
                headerLayout={getColumns()}
                renderListItem={({ item }) => (
                  <ListItem
                    key={item.id}
                    name={`PIT Challenge #${item.id}`}
                    item={item}
                    isPackage={false}
                    onClick={() => handleRowClick(item)}
                    style={{ marginBottom: 12, cursor: "pointer" }}
                  >
                    <Row gutter={[8, 0]} style={{ width: '100%' }}>
                      <Col {...subProjectLayout} className="contentEllipse">
                        <div className="mobile-label">Sub-Project</div>
                        {item.sub_project || 'N/A'}
                      </Col>
                      <Col {...challengesLayout} className="contentEllipse">
                        <div className="mobile-label">Challenge</div>
                        {item.challenge || 'No challenge description available'}
                      </Col>
                      <Col {...actionTakenLayout} className="contentEllipse">
                        <div className="mobile-label">Action Taken</div>
                        {item.action_taken || 'No action taken yet'}
                      </Col>
                      <Col {...reportDateLayout} style={{ width: '100%', textAlign: 'center', justifyContent: 'center', paddingLeft: '12px' }} className="contentEllipse">
                        <div className="mobile-label">Report Date</div>
                        {item.visit_date ? moment(item.visit_date).format('YYYY-MM-DD') : 'No report date available'}
                      </Col>
                    </Row>
                  </ListItem>
                )}
                showToolbar={false}
              />
              )}
            </div>
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PCT" ? "#ED8936" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#ED8936" }} className="tab-icon">
                  PCT
                </span>
                PCT Report
              </span>
            }
            key="PCT"
          >
            {getTabContent("PCT")}
            <div className="pct-table-container">
              {tabData.length === 0 && !isLoading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  color: '#666',
                  fontSize: '16px'
                }}>
                  No challenges data available for the selected criteria.
                </div>
              ) : (
                <CustomList
                itemName="Challenges"
                items={tabData}
                page={1}
                itemCount={tabData.length}
                loading={isLoading}
                headerLayout={getColumns()}
                renderListItem={({ item }) => (
                  <ListItem
                    key={item.id}
                    name={`PCT Challenge #${item.id}`}
                    item={item}
                    isPackage={false}
                    onClick={() => handleRowClick(item)}
                    style={{ marginBottom: 12, cursor: "pointer" }}
                  >
                    <Row gutter={[8, 0]} style={{ width: '100%' }}>
                      <Col {...subProjectLayout} className="contentEllipse">
                        <div className="mobile-label">Sub-Project</div>
                        {item.sub_project || 'N/A'}
                      </Col>
                      <Col {...challengesLayout} className="contentEllipse">
                        <div className="mobile-label">Challenge</div>
                        {item?.challenge || item?.challenge_description || 'No challenge description available'}
                      </Col>
                      <Col {...actionTakenLayout} className="contentEllipse">
                        <div className="mobile-label">Action Taken</div>
                        {item.action_taken || 'No action taken yet'}
                      </Col>
                      <Col {...reportDateLayout} style={{ width: '100%', textAlign: 'center', justifyContent: 'center', paddingLeft: '12px' }} className="contentEllipse">
                        <div className="mobile-label">Report Date</div>
                        {item.visit_date ? moment(item.visit_date).format('YYYY-MM-DD') : 'No report date available'}
                      </Col>
                    </Row>
                  </ListItem>
                )}
                showToolbar={false}
              />
              )}
            </div>
          </TabPane>
        </Tabs>

        <Drawer
          title="Challenge Details"
          placement="right"
          visible={drawerVisible}
          onClose={handleCloseDrawer}
          width={600}
        >
          <p>
            <strong>Sub-Project:</strong> {activeItem?.sub_project || 'N/A'}
          </p>
          <p>
            <strong>Challenge:</strong> {activeItem?.challenge || activeItem?.challenge_description || 'No challenge description'}
          </p>
          {activeItem?.action_taken && (
            <p>
              <strong>Action Taken:</strong> {activeItem.action_taken}
            </p>
          )}
          {activeItem?.pit_report?.staff_name && (
            <p>
              <strong>Reported by:</strong> {activeItem.pit_report.staff_name}
            </p>
          )}
          {activeItem?.pit_report?.section && (
            <p>
              <strong>Section:</strong> {activeItem.pit_report.section}
            </p>
          )}
          {activeItem?.pit_report?.visit_date && (
            <p>
              <strong>Visit Date:</strong> {moment(activeItem.pit_report.visit_date).format('YYYY-MM-DD')}
            </p>
          )}
          {activeItem?.latitude && activeItem?.longitude && (
            <p>
              <strong>Location:</strong> {activeItem.latitude}, {activeItem.longitude}
            </p>
          )}
          {activeItem?.created_at && (
            <p>
              <strong>Created:</strong> {moment(activeItem.created_at).format('YYYY-MM-DD')}
            </p>
          )}
          {activeItem?.updated_at && (
            <p>
              <strong>Updated:</strong> {moment(activeItem.updated_at).format('YYYY-MM-DD')}
            </p>
          )}
          {activeTab === "PCT" && activeItem?.sub_projects_visited && activeItem.sub_projects_visited.length > 0 && (
            <p>
              <strong>Sub-Projects Visited:</strong> {activeItem.sub_projects_visited.map(sp => sp.sub_project?.name).join(', ')}
            </p>
          )}
          {activeTab === "PCT" && activeItem?.pct_report?.staff_name && (
            <p>
              <strong>Reported by:</strong> {activeItem.pct_report.staff_name}
            </p>
          )}
          {activeTab === "PCT" && activeItem?.user?.email && (
            <p>
              <strong>Email:</strong> {activeItem.user.email}
            </p>
          )}
          {activeTab === "PCT" && activeItem?.pct_report?.section && (
            <p>
              <strong>Work Section:</strong> {activeItem.pct_report.section}
            </p>
          )}
          {activeTab === "PIT" && (activeItem?.staff_name || activeItem?.reported_by) && (
            <p>
              <strong>Reported by:</strong> {activeItem.staff_name || activeItem.reported_by}
            </p>
          )}
          {activeTab === "PIT" && (activeItem?.email || activeItem?.user?.email) && (
            <p>
              <strong>Email:</strong> {activeItem.email || activeItem.user?.email}
            </p>
          )}
          {activeTab === "PIT" && (activeItem?.section || activeItem?.works_category) && (
            <p>
              <strong>Work Section:</strong> {activeItem.section || activeItem.works_category}
            </p>
          )}
          {activeTab === "PIT" && activeItem?.pit_report?.report_date && (
            <p>
              <strong>Report Date:</strong> {activeItem.pit_report.report_date}
            </p>
          )}
          {activeTab === "PCT" && activeItem?.pct_report?.report_date && (
            <p>
              <strong>Report Date:</strong> {activeItem.pct_report.report_date}
            </p>
          )}
          {activeTab === "CSC_CHALLENGES" && activeItem?.package?.name && (
            <p>
              <strong>Package:</strong> {activeItem.package.name}
            </p>
          )}
          {activeTab === "CSC_CHALLENGES" && activeItem?.works_category && (
            <p>
              <strong>Works Category:</strong> {activeItem.works_category}
            </p>
          )}
          {activeTab === "CSC_CHALLENGES" && activeItem?.report_start_date && activeItem?.report_end_date && (
            <p>
              <strong>Report Period:</strong> {isoDateToHumanReadableDate(activeItem.report_start_date)} - {isoDateToHumanReadableDate(activeItem.report_end_date)}
            </p>
          )}
          {activeItem?.way_forward && (
            <p>
              <strong>Way Forward:</strong> {activeItem.way_forward}
            </p>
          )}
          <div>
            <h2
              style={{
                marginBottom: "12px",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Photos uploaded by {activeTab}
            </h2>
            <Row gutter={[16, 16]}>
              {activeItem?.photo_url ? (
                <Col xs={24} sm={12} md={8} lg={8}>
                  <div>
                    <Image
                      width="100%"
                      style={{
                        cursor: "pointer",
                        maxHeight: "200px",
                        objectFit: "cover",
                      }}
                      src={activeItem?.photo_url}
                      preview={false}
                      onClick={() => openSinglePreview(activeItem?.photo_url)}
                    />
                    <figcaption className="image-caption">
                      {activeItem?.caption}
                    </figcaption>
                  </div>
                </Col>
              ) : (
                <Col xs={24} sm={12} md={8} lg={8}>
                  <div
                    style={{
                      width: "100%",
                      height: "200px",
                      backgroundColor: "#f0f0f0",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "16px",
                      color: "#999",
                    }}
                  >
                    No image Available
                  </div>
                </Col>
              )}
            </Row>

            {/* Modal for single image preview */}
            <Modal
              visible={singlePreviewVisible}
              footer={null}
              onCancel={handleSingleCancel}
              width="50%"
            >
              <img
                alt="Example"
                style={{ width: "100%" }}
                src={previewImage}
              />
            </Modal>
          </div>
        </Drawer>
      </Card>
    </div>
  );
};

export default ChallengesImplementations;
